import { api } from './api'
import { CaregiverData, ChildData } from '../types/user'

export interface UserProfileData {
  caregiver: CaregiverData
  children: ChildData[]
}

export interface UserProfile {
  id: string
  email: string
  display_name: string
  preferred_language: string
  subscription_status: string
  created_at: string
  updated_at: string
  children: Child[]
}

export interface Child {
  id: string
  parent_id: string
  name: string
  birth_year: string
  experience_description: string
  interests: string[]
  avatar_url?: string
  preferredStoryLanguage?: string

  // Superpower system
  selected_superpower_name?: string
  selected_superpower_description?: string
  superpower_is_custom?: boolean
  generated_superpower_options?: any[]

  // Internal diagnosis flags (not shown to users)
  diagnosis_flags?: any

  created_at: string
  updated_at?: string
}

/**
 * Get current user profile from the backend API
 */
export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  try {
    const userProfile = await api.getCurrentUser()

    return userProfile
  } catch (error) {
    //console.error('❌ [DEBUG] Error getting user profile:', error);
    //console.error('❌ [DEBUG] Error type:', typeof error);
    //console.error('❌ [DEBUG] Error message:', (error as any)?.message);
    //console.error('❌ [DEBUG] Error response:', (error as any)?.response);
    //console.error('❌ [DEBUG] Error config:', (error as any)?.config);

    // If user doesn't exist (404), return null
    if ((error as any)?.response?.status === 404) {
      return null
    }

    throw error
  }
}

/**
 * Check if user has completed onboarding by checking if they have children
 */
export async function checkOnboardingStatus(uid: string): Promise<boolean> {
  try {
    const userProfile = await getCurrentUserProfile()
    const profileData = (userProfile as any)?.data || userProfile
    const childrenArray = profileData?.children || profileData?.Children || []

    // If user exists and has children, onboarding is completed
    const isCompleted = !!(childrenArray && childrenArray.length > 0)

    return isCompleted
  } catch (error) {
    //console.error('❌ [DEBUG] Error checking onboarding status via backend:', error);
    //console.error('❌ [DEBUG] Error type:', typeof error);
    //console.error('❌ [DEBUG] Error message:', (error as any)?.message);
    //console.error('❌ [DEBUG] Error response status:', (error as any)?.response?.status);
    //console.error('❌ [DEBUG] Error response data:', (error as any)?.response?.data);
    //console.error('❌ [DEBUG] Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));

    // If user doesn't exist (404), onboarding is not completed
    if ((error as any)?.response?.status === 404) {
      return false
    }

    // If authentication error (401/403), onboarding status unknown
    if ((error as any)?.response?.status === 401 || (error as any)?.response?.status === 403) {
      return false
    }

    // For other errors, default to false (don't crash the app)
    return false
  }
}

/**
 * Submit onboarding data to create user and children profiles
 */
export async function submitOnboardingData(onboardingData: any): Promise<any> {
  try {
    const result = await api.submitOnboarding(onboardingData)
    return result
  } catch (error) {
    //console.error('❌ Error submitting onboarding data:', error);
    throw error
  }
}

/**
 * Get user's children from the backend API
 */
export async function getUserChildren(): Promise<Child[]> {
  try {
    const userProfile = await getCurrentUserProfile()
    return userProfile?.children || []
  } catch (error) {
    //console.error('❌ Error getting user children:', error);
    throw error
  }
}

/**
 * Create a new child profile
 */
export async function createChildProfile(childData: any): Promise<Child> {
  try {
    // Note: This would use a dedicated children API endpoint when available
    // For now, children are created as part of onboarding
    throw new Error('Child creation outside of onboarding not yet implemented')
  } catch (error) {
    //console.error('❌ Error creating child profile:', error);
    throw error
  }
}

/**
 * Update a child profile
 */
export async function updateChildProfile(childId: string, updates: Partial<Child>): Promise<Child> {
  try {
    // Note: This would use the PUT /users/me/children/{child_id} endpoint
    throw new Error('Child update functionality not yet implemented in API service')
  } catch (error) {
    //console.error('❌ Error updating child profile:', error);
    throw error
  }
}

/**
 * Delete a child profile
 */
export async function deleteChildProfile(childId: string): Promise<void> {
  try {
    // Note: This would use the DELETE /users/me/children/{child_id} endpoint
    throw new Error('Child deletion functionality not yet implemented in API service')
  } catch (error) {
    //console.error('❌ Error deleting child profile:', error);
    throw error
  }
}

/**
 * Update current user profile
 */
export async function updateUserProfile(updates: any): Promise<UserProfile> {
  try {
    // Note: This would use the PUT /users/me endpoint
    throw new Error('User update functionality not yet implemented in API service')
  } catch (error) {
    //console.error('❌ Error updating user profile:', error);
    throw error
  }
}

// Legacy function compatibility (deprecated - use API functions above)
export async function saveUserProfile(uid: string, profileData: UserProfileData): Promise<void> {
  console.warn('🚨 saveUserProfile is deprecated. Use submitOnboardingData() instead.')
  throw new Error('saveUserProfile is deprecated. Use submitOnboardingData() instead.')
}

export async function getUserProfile(uid: string): Promise<UserProfileData | null> {
  console.warn('🚨 getUserProfile is deprecated. Use getCurrentUserProfile() instead.')
  throw new Error('getUserProfile is deprecated. Use getCurrentUserProfile() instead.')
}

export async function updateCaregiverData(
  uid: string,
  data: Partial<CaregiverData>
): Promise<void> {
  console.warn('🚨 updateCaregiverData is deprecated. Use updateUserProfile() instead.')
  throw new Error('updateCaregiverData is deprecated. Use updateUserProfile() instead.')
}

export async function updateChildData(
  uid: string,
  childId: string,
  data: Partial<ChildData>
): Promise<void> {
  console.warn('🚨 updateChildData is deprecated. Use updateChildProfile() instead.')
  throw new Error('updateChildData is deprecated. Use updateChildProfile() instead.')
}

export async function testFirestoreConnection(): Promise<void> {
  console.warn('🚨 testFirestoreConnection is deprecated. Firestore is no longer used.')
  throw new Error('testFirestoreConnection is deprecated. Firestore is no longer used.')
}
